import React from "react";
import ServerError from "@/pages/ServerError";

/**
 * Top-level error boundary. When any React component throws during render,
 * this catches the error and shows the custom <ServerError> page instead of
 * a blank white screen. Logs the error to the console (and the analytics
 * pipeline if available) for debugging.
 *
 * Provides an onRetry callback to <ServerError> that resets the boundary -
 * useful when the error was transient (e.g. a one-off network glitch).
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
        this.handleRetry = this.handleRetry.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        console.error("ErrorBoundary caught:", error, info);
        try {
            window.dispatchEvent(
                new CustomEvent("app:error-boundary", {
                    detail: { message: String(error?.message || error), info },
                })
            );
        } catch {
            /* ignore */
        }
    }

    handleRetry() {
        this.setState({ hasError: false, error: null });
    }

    render() {
        if (this.state.hasError) {
            return (
                <ServerError
                    error={this.state.error}
                    onRetry={this.handleRetry}
                />
            );
        }
        return this.props.children;
    }
}
